import { useState } from 'react';
import { Button } from '../../../components/ui/button.tsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog.tsx';
import { Input } from '../../../components/ui/input.tsx';
import { Label } from '../../../components/ui/label.tsx';
import { Badge } from '../../../components/ui/badge.tsx';
import {
  CreditCard,
  Download,
  Edit,
  MapPin,
  Calendar,
  DollarSign,
  AlertTriangle,
} from 'lucide-react';

export default function SubscriptionManagement() {
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showEditAddressModal, setShowEditAddressModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('pro');

  // Mock data
  const currentPlan = {
    name: 'Pro Plan',
    price: 29.99,
    billingCycle: 'monthly',
    nextBilling: '2024-09-15',
    features: [
      'Advanced Analytics',
      'Priority Support',
      'Custom Integrations',
      'Unlimited Users',
    ],
  };

  const billingAddress = {
    name: 'John Doe',
    company: 'Acme Corp',
    address: '123 Business St',
    city: 'San Francisco',
    state: 'CA',
    zip: '94105',
    country: 'United States',
  };

  const paymentMethods = [
    {
      id: '1',
      type: 'card',
      brand: 'Visa',
      last4: '4242',
      expiryMonth: 12,
      expiryYear: 2025,
      isDefault: true,
    },
    {
      id: '2',
      type: 'card',
      brand: 'Mastercard',
      last4: '8888',
      expiryMonth: 8,
      expiryYear: 2026,
      isDefault: false,
    },
  ];

  const billingHistory = [
    {
      id: '1',
      date: '2024-08-15',
      amount: 29.99,
      status: 'paid',
      plan: 'Pro Plan',
      receiptUrl: '#',
    },
    {
      id: '2',
      date: '2024-07-15',
      amount: 29.99,
      status: 'paid',
      plan: 'Pro Plan',
      receiptUrl: '#',
    },
    {
      id: '3',
      date: '2024-06-15',
      amount: 29.99,
      status: 'paid',
      plan: 'Pro Plan',
      receiptUrl: '#',
    },
  ];

  const availablePlans = [
    {
      id: 'basic',
      name: 'Basic Plan',
      price: 9.99,
      features: ['Basic Analytics', 'Email Support', '5 Users'],
    },
    {
      id: 'pro',
      name: 'Pro Plan',
      price: 29.99,
      features: [
        'Advanced Analytics',
        'Priority Support',
        'Custom Integrations',
        'Unlimited Users',
      ],
    },
    {
      id: 'enterprise',
      name: 'Enterprise Plan',
      price: 99.99,
      features: [
        'Everything in Pro',
        'Dedicated Support',
        'Custom Development',
        'SLA Guarantee',
      ],
    },
  ];

  const handleDownloadReceipt = (receiptUrl) => {
    // Mock download functionality
    console.log('Downloading receipt:', receiptUrl);
  };

  const handleChangePlan = () => {
    console.log('Changing plan to:', selectedPlan);
    setShowChangePlanModal(false);
  };

  const handleCancelSubscription = () => {
    console.log('Cancelling subscription');
    setShowCancelModal(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Subscription Management</h1>
      </div>

      {/* Current Plan and Billing Address - Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-[8fr_4fr] gap-6">
        {/* Current Plan */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-purple-600" />
              Current Plan
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">{currentPlan.name}</h3>
                <p className="text-muted-foreground">
                  ${currentPlan.price}/{currentPlan.billingCycle}
                </p>
              </div>
              <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-200">
                Active
              </Badge>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Next Billing Date</Label>
                <p className="flex items-center gap-2 mt-1">
                  <Calendar className="h-4 w-4 text-purple-600" />
                  {currentPlan.nextBilling}
                </p>
              </div>
              <div>
                <Label className="text-sm font-medium">Features</Label>
                <ul className="mt-1 space-y-1">
                  {currentPlan.features.map((feature, index) => (
                    <li key={index} className="text-sm text-muted-foreground">
                      • {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={() => setShowChangePlanModal(true)}
                className="bg-purple-600 hover:bg-purple-700"
              >
                Change Plan
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowCancelModal(true)}
                className="border-red-200 text-red-600 hover:bg-red-50"
              >
                Cancel Subscription
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Billing Address */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-purple-600" />
              Billing Address
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <p className="font-medium">{billingAddress.name}</p>
              {billingAddress.company && <p>{billingAddress.company}</p>}
              <p>{billingAddress.address}</p>
              <p>
                {billingAddress.city}, {billingAddress.state}{' '}
                {billingAddress.zip}
              </p>
              <p>{billingAddress.country}</p>
            </div>
            <Button
              variant="outline"
              onClick={() => setShowEditAddressModal(true)}
              className="mt-4 border-purple-200 text-purple-600 hover:bg-purple-50"
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit Address
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Payment Methods and Billing History - Side by Side */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Payment Methods */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-purple-600" />
              Payment Methods
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {paymentMethods.map((method) => (
              <div
                key={method.id}
                className="flex items-center justify-between p-4 border rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <CreditCard className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">
                      {method.brand} •••• {method.last4}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Expires {method.expiryMonth}/{method.expiryYear}
                    </p>
                  </div>
                  {method.isDefault && (
                    <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-200">
                      Default
                    </Badge>
                  )}
                </div>
                <Button variant="outline" size="sm">
                  Edit
                </Button>
              </div>
            ))}
            <Button
              onClick={() => setShowAddPaymentModal(true)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Add Payment Method
            </Button>
          </CardContent>
        </Card>

        {/* Billing History */}
        <Card>
          <CardHeader>
            <CardTitle>Billing History</CardTitle>
            <CardDescription>
              View and download your past invoices
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {billingHistory.map((bill) => (
                <div
                  key={bill.id}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="font-medium">{bill.plan}</p>
                      <p className="text-sm text-muted-foreground">
                        {bill.date}
                      </p>
                    </div>
                    <Badge
                      className={
                        bill.status === 'paid'
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : bill.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                          : 'bg-red-100 text-red-800 hover:bg-red-200'
                      }
                    >
                      {bill.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-medium">${bill.amount}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadReceipt(bill.receiptUrl)}
                      className="border-purple-200 text-purple-600 hover:bg-purple-50"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Receipt
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Change Plan Modal */}
      <Dialog open={showChangePlanModal} onOpenChange={setShowChangePlanModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Change Your Plan</DialogTitle>
            <DialogDescription>
              Choose a plan that best fits your needs
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            {availablePlans.map((plan) => (
              <div
                key={plan.id}
                className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                  selectedPlan === plan.id
                    ? 'border-purple-600 bg-purple-50'
                    : 'border-gray-200 hover:border-purple-300'
                }`}
                onClick={() => setSelectedPlan(plan.id)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold">{plan.name}</h3>
                    <p className="text-2xl font-bold text-purple-600">
                      ${plan.price}/month
                    </p>
                  </div>
                  <input
                    type="radio"
                    checked={selectedPlan === plan.id}
                    onChange={() => setSelectedPlan(plan.id)}
                    className="text-purple-600"
                  />
                </div>
                <ul className="mt-2 space-y-1">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="text-sm text-muted-foreground">
                      • {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowChangePlanModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleChangePlan}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Change Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Cancel Subscription
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel your subscription? You'll lose
              access to all premium features at the end of your current billing
              period.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-red-50 p-4 rounded-lg">
            <p className="text-sm text-red-800">
              <strong>What happens when you cancel:</strong>
            </p>
            <ul className="mt-2 text-sm text-red-700 space-y-1">
              <li>
                • Your subscription will remain active until{' '}
                {currentPlan.nextBilling}
              </li>
              <li>• You'll lose access to premium features after that date</li>
              <li>• Your data will be preserved for 30 days</li>
              <li>• You can reactivate anytime before then</li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelModal(false)}>
              Keep Subscription
            </Button>
            <Button
              onClick={handleCancelSubscription}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Cancel Subscription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Payment Method Modal */}
      <Dialog open={showAddPaymentModal} onOpenChange={setShowAddPaymentModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Payment Method</DialogTitle>
            <DialogDescription>
              Add a new credit card to your account
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="cardNumber">Card Number</Label>
              <Input id="cardNumber" placeholder="1234 5678 9012 3456" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="expiry">Expiry Date</Label>
                <Input id="expiry" placeholder="MM/YY" />
              </div>
              <div>
                <Label htmlFor="cvc">CVC</Label>
                <Input id="cvc" placeholder="123" />
              </div>
            </div>
            <div>
              <Label htmlFor="cardName">Name on Card</Label>
              <Input id="cardName" placeholder="John Doe" />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAddPaymentModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => setShowAddPaymentModal(false)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Add Payment Method
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Address Modal */}
      <Dialog
        open={showEditAddressModal}
        onOpenChange={setShowEditAddressModal}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Billing Address</DialogTitle>
            <DialogDescription>
              Update your billing address information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input id="name" defaultValue={billingAddress.name} />
            </div>
            <div>
              <Label htmlFor="company">Company (Optional)</Label>
              <Input id="company" defaultValue={billingAddress.company} />
            </div>
            <div>
              <Label htmlFor="address">Address</Label>
              <Input id="address" defaultValue={billingAddress.address} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city">City</Label>
                <Input id="city" defaultValue={billingAddress.city} />
              </div>
              <div>
                <Label htmlFor="state">State</Label>
                <Input id="state" defaultValue={billingAddress.state} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="zip">ZIP Code</Label>
                <Input id="zip" defaultValue={billingAddress.zip} />
              </div>
              <div>
                <Label htmlFor="country">Country</Label>
                <Input id="country" defaultValue={billingAddress.country} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditAddressModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => setShowEditAddressModal(false)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Save Address
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
